import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Leaf, PackageCheck, MessagesSquare } from "lucide-react";
import { storeApi } from "@/lib/storefront";
import ProductCard from "@/components/storefront/ProductCard";
import HeroVideo from "@/components/storefront/HeroVideo";
import SEO from "@/components/storefront/SEO";
import useStoreRefresh from "@/hooks/useStoreRefresh";

const TESTIMONIALS = [
  {
    quote: "Everything from Luna. Creative Home feels considered and quietly beautiful. My morning table setting has never looked calmer.",
    author: "Emma L.",
    location: "Stockholm",
  },
  {
    quote: "I ordered a set of linen napkins and the packaging alone made my week. It's the small things, done right.",
    author: "Sophie M.",
    location: "Paris",
  },
  {
    quote: "Finally a shop that understands 'less, but better'. Fast delivery, perfect quality, no fuss.",
    author: "Mika T.",
    location: "Copenhagen",
  },
];

export default function Home() {
  const [data, setData] = useState(null);
  const [tree, setTree] = useState([]);

  // Home payload (hero, featured, categories grid, testimonials) —
  // refetches on focus so newly added/removed categories appear live.
  useStoreRefresh(() => {
    storeApi.get("/home")
      .then((r) => setData(r.data))
      .catch((err) => console.error("Home load:", err));
    storeApi.get("/categories/tree")
      .then((r) => setTree(r.data || []))
      .catch((err) => console.error("Tree load:", err));
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        title=""
        description="Luna. Creative Home — a home where creativity grows. Thoughtfully curated craft kits, quietly beautiful decor, and storage that makes family life feel lighter. Free delivery over $75, 30-day returns, ships across the United States."
      />

      {/* HERO — video, premium */}
      <HeroVideo />

      {/* USP STRIP — For creative kids + cozy family homes */}
      <section className="border-y border-[#DDD6C5] bg-white py-12 md:py-16" data-testid="usp-strip">
        <div className="mx-auto grid max-w-[1440px] gap-8 px-6 md:grid-cols-4 md:gap-12 md:px-10">
          {[
            [Sparkles, "For curious kids", "Craft kits, toys and creativity sets that last"],
            [Leaf, "Cozy at home", "Textiles, lighting and decor for calmer rooms"],
            [PackageCheck, "Organized family life", "Storage and everyday pieces that make sense"],
            [MessagesSquare, "Real humans", "Email us anytime — we reply within a day"],
          ].map(([Icon, title, body], i) => (
            <div key={title} className={`flex items-start gap-4 store-fade-up store-fade-up-d${Math.min(i, 4)}`}>
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-[#E8E2D2] text-[#7D8471]">
                <Icon size={22} strokeWidth={1.5} />
              </div>
              <div>
                <div className="font-display text-xl leading-tight text-black">{title}</div>
                <div className="mt-1.5 text-[15px] leading-relaxed text-[#1F1A14]">{body}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES — Shop by creativity & home: 2x2 tiles on mobile, 4-col panel with subcategory lists on desktop */}
      {tree.length > 0 && (
        <section className="border-b border-[#DDD6C5] bg-[#F2EDDF] py-12 md:py-20" data-testid="browse-by-category">
          <div className="mx-auto max-w-[1440px] px-5 md:px-10">
            <div className="mb-6 flex items-end justify-between gap-4 md:mb-10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#2D2823] md:text-xs">Categories</div>
                <h2 className="mt-2 font-display text-[28px] leading-tight text-[#1A1A1A] md:text-5xl">
                  Shop by <span className="font-display-soft italic">creativity</span> & home
                </h2>
              </div>
              <Link to="/catalog" className="hidden items-center gap-1 text-sm text-[#7D8471] hover:underline md:inline-flex">All categories <ArrowRight size={14} /></Link>
            </div>

            {/* MOBILE — 2x2 photo tiles */}
            <div className="grid grid-cols-2 gap-3 md:hidden">
              {tree.slice(0, 4).map((c) => (
                <Link
                  key={c.id}
                  to={`/c/${c.slug}`}
                  className="group relative block aspect-[3/4] overflow-hidden rounded-md bg-[#E8E2D2]"
                  data-testid={`browse-tile-${c.slug}`}
                >
                  {c.image && (
                    <img src={c.image} alt={c.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]" loading="lazy" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-end p-3">
                    <div className="font-display text-[18px] uppercase leading-tight tracking-[0.04em] text-white drop-shadow">{c.name}</div>
                  </div>
                  <div className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#1A1A1A] shadow-[0_4px_14px_-4px_rgba(0,0,0,0.4)]">
                    <ArrowRight size={14} />
                  </div>
                </Link>
              ))}
            </div>

            {/* DESKTOP — 4 columns: image + parent name + subcategory list */}
            <div className="hidden gap-x-6 gap-y-10 md:grid md:grid-cols-2 lg:grid-cols-4">
              {tree.slice(0, 4).map((c) => (
                <div key={c.id} className="space-y-4" data-testid={`browse-col-${c.slug}`}>
                  <Link to={`/c/${c.slug}`} className="group block">
                    {c.image ? (
                      <div className="aspect-[4/3] overflow-hidden rounded-md bg-white">
                        <img src={c.image} alt={c.name} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" loading="lazy" />
                      </div>
                    ) : (
                      <div className="aspect-[4/3] rounded-md bg-[#E8E2D2]" />
                    )}
                    <div className="mt-3 font-display text-[20px] font-medium uppercase tracking-[0.18em] text-[#1A1A1A] group-hover:text-[#7D8471]">
                      {c.name}
                    </div>
                  </Link>
                  {c.children?.length > 0 && (
                    <ul className="space-y-2 text-[14px] text-[#2D2823]">
                      {c.children.slice(0, 7).map((sub) => (
                        <li key={sub.id}>
                          <Link to={`/c/${sub.slug}`} className="hover:text-[#7D8471] hover:underline" data-testid={`browse-sub-${sub.slug}`}>
                            {sub.name}
                          </Link>
                        </li>
                      ))}
                      {c.children.length > 7 && (
                        <li>
                          <Link to={`/c/${c.slug}`} className="text-[13px] font-medium text-[#7D8471] hover:underline">
                            View all {c.children.length} →
                          </Link>
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LIFESTYLE — large editorial photo + thoughtful copy */}
      <section className="border-b border-[#DDD6C5] bg-[#E8E2D2] py-20 md:py-28">
        <div className="mx-auto grid max-w-[1440px] gap-10 px-6 md:grid-cols-[1.1fr_1fr] md:items-center md:gap-20 md:px-10">
          <div className="order-2 md:order-1">
            <div className="aspect-[5/4] overflow-hidden rounded-md bg-white">
              <img src="https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?w=1400&auto=format&fit=crop&q=85" alt="A calm living room with a green velvet sofa" className="h-full w-full object-cover" />
            </div>
          </div>
          <div className="order-1 md:order-2">
            <div className="mb-3 text-xs uppercase tracking-[0.3em] text-[#2D2823]">Lifestyle note</div>
            <h2 className="font-display text-4xl leading-[1.1] text-[#1A1A1A] md:text-[60px]">
              Organised home. <br />
              Calm routine. <br />
              <span className="font-display-soft italic">Beautiful</span> little things.
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-[#2D2823]">
              We believe a calmer home starts with small, intentional choices. Each piece in our edit earns its place — useful, considered, and softly beautiful.
            </p>
            <Link to="/pages/our-story" className="mt-6 inline-flex items-center gap-2 text-sm text-[#1A1A1A] underline decoration-[#7D8471] underline-offset-4 hover:text-[#7D8471]">
              Our approach <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {data?.featured?.length > 0 && (
        <section className="border-b border-[#DDD6C5] bg-[#F2EDDF] py-20 md:py-28">
          <div className="mx-auto max-w-[1440px] px-6 md:px-10">
            <div className="mb-10 flex items-end justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-[#2D2823]">Selected for you</div>
                <h2 className="mt-2 font-display text-4xl text-[#1A1A1A] md:text-5xl">Best <span className="font-display-soft">sellers</span></h2>
              </div>
              <Link to="/catalog" className="hidden items-center gap-1 text-sm text-[#7D8471] hover:underline md:inline-flex">See all <ArrowRight size={14} /></Link>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-3 lg:grid-cols-4">
              {data.featured.slice(0, 8).map((p, i) => <ProductCard key={p.id} product={p} index={i % 4} />)}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      <section className="border-b border-[#DDD6C5] bg-[#E8E2D2] py-20 md:py-28" data-testid="testimonials">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="mb-14 text-center">
            <div className="text-xs uppercase tracking-[0.3em] text-[#2D2823]">In their words</div>
            <h2 className="mt-2 font-display text-4xl md:text-5xl">Quiet praise from our <span className="font-display-soft">circle</span></h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <figure key={t.author} className={`rounded-md border border-[#DDD6C5] bg-white p-8 store-fade-up store-fade-up-d${i}`}>
                <div className="mb-4 font-display text-4xl leading-none text-[#7D8471]">"</div>
                <blockquote className="font-display text-lg leading-relaxed text-[#1A1A1A]">
                  {t.quote}
                </blockquote>
                <figcaption className="mt-6 border-t border-[#DDD6C5] pt-4 text-xs uppercase tracking-[0.2em] text-[#2D2823]">
                  {t.author} · {t.location}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      {data?.new_arrivals?.length > 0 && (
        <section className="border-b border-[#DDD6C5] bg-[#F2EDDF] py-20 md:py-28">
          <div className="mx-auto max-w-[1440px] px-6 md:px-10">
            <div className="mb-10 flex items-end justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-[#2D2823]">Just arrived</div>
                <h2 className="mt-2 font-display text-4xl text-[#1A1A1A] md:text-5xl">New <span className="font-display-soft">this week</span></h2>
              </div>
              <Link to="/catalog?sort=-created_at" className="hidden items-center gap-1 text-sm text-[#7D8471] hover:underline md:inline-flex">All new <ArrowRight size={14} /></Link>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-3 lg:grid-cols-4">
              {data.new_arrivals.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
